import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";

function PhonePeTransactions() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [mandateFilter, setMandateFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [summary, setSummary] = useState({
    total: 0,
    successCount: 0,
    failedCount: 0,
    totalSuccessAmount: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    pausedSubscriptions: 0,
    revokedSubscriptions: 0,
  });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const pageSize = 50;

  const axiosAPI = useAxios();

  const fetchTransactions = async (targetPage = page) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: targetPage,
        limit: pageSize,
        search: search ? search.trim() : "",
        status: statusFilter || "ALL",
        mandate_status: mandateFilter || "ALL",
        from_date: fromDate || "",
        to_date: toDate || "",
      });

      const res = await axiosAPI.get(
        `/api/cronjob/phonepe-subscription-transactions?${queryParams.toString()}`,
      );

      if (res.data && res.data.success) {
        setTransactions(res.data.results || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalFiltered(res.data.total || 0);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
      }
    } catch (err) {
      console.error("Error fetching PhonePe transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(page);
  }, [page, statusFilter, mandateFilter, fromDate, toDate]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    fetchTransactions(1);
  };

  const handleResetFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setMandateFilter("ALL");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const handleOpenModal = (tx) => {
    setSelectedTransaction(tx);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedTransaction(null);
    setShowModal(false);
  };

  const renderTxnBadge = (status) => {
    switch (status) {
      case "SUCCESS":
        return (
          <span className="badge bg-success">
            <i className="fas fa-check-circle me-1"></i>SUCCESS
          </span>
        );
      case "FAILED":
        return (
          <span className="badge bg-danger">
            <i className="fas fa-times-circle me-1"></i>FAILED
          </span>
        );
      case "PAUSED":
        return (
          <span className="badge bg-warning text-dark">
            <i className="fas fa-pause-circle me-1"></i>PAUSED
          </span>
        );
      case "ACTIVE":
        return (
          <span className="badge bg-info text-dark">
            <i className="fas fa-play-circle me-1"></i>RESUMED
          </span>
        );
      case "REVOKED":
        return (
          <span className="badge bg-secondary">
            <i className="fas fa-ban me-1"></i>REVOKED
          </span>
        );
      default:
        return <span className="badge bg-dark">{status || "N/A"}</span>;
    }
  };

  const renderMandateBadge = (mandateStatus) => {
    switch (mandateStatus) {
      case "ACTIVE":
        return (
          <span className="badge bg-success-subtle text-success border border-success">
            <i className="fas fa-check me-1"></i>ACTIVE
          </span>
        );
      case "PAUSED":
        return (
          <span className="badge bg-warning-subtle text-warning border border-warning">
            <i className="fas fa-pause me-1"></i>PAUSED
          </span>
        );
      case "REVOKED":
        return (
          <span className="badge bg-secondary-subtle text-secondary border border-secondary">
            <i className="fas fa-ban me-1"></i>REVOKED
          </span>
        );
      case "FAILED":
        return (
          <span className="badge bg-danger-subtle text-danger border border-danger">
            <i className="fas fa-times me-1"></i>FAILED
          </span>
        );
      case "PENDING":
        return (
          <span className="badge bg-light text-muted border">
            <i className="fas fa-clock me-1"></i>PENDING
          </span>
        );
      default:
        return <span className="text-muted small">N/A</span>;
    }
  };

  const formatPayload = (payload) => {
    if (!payload) return "No payload data";
    try {
      const parsed =
        typeof payload === "string" ? JSON.parse(payload) : payload;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return String(payload);
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        {/* Stat Cards */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div className="d-flex flex-column flex-md-row gap-3 w-100">
            {[
              {
                title: "Total Attempts",
                value: summary.total,
                icon: "fas fa-list-alt",
                colorClass: "icon-primary",
                textClass: "",
              },
              {
                title: "Successful",
                value: summary.successCount,
                icon: "fas fa-check-circle",
                colorClass: "icon-success",
                textClass: "text-success",
              },
              {
                title: "Failed",
                value: summary.failedCount,
                icon: "fas fa-exclamation-triangle",
                colorClass: "icon-danger",
                textClass: "text-danger",
              },
              {
                title: "Active Mandates",
                value: summary.activeSubscriptions || 0,
                icon: "fas fa-user-check",
                colorClass: "icon-info",
                textClass: "text-info",
              },
              {
                title: "Total Collected",
                value: `₹${Number(summary.totalSuccessAmount || 0).toLocaleString()}`,
                icon: "fas fa-donate",
                colorClass: "icon-warning",
                textClass: "",
              },
            ].map((stat, idx) => (
              <div key={idx} className="flex-fill">
                <div className="card card-stats card-round mb-0">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-icon">
                        <div
                          className={`icon-big text-center ${stat.colorClass} border bubble-shadow-small`}
                        >
                          <i className={stat.icon}></i>
                        </div>
                      </div>
                      <div className="col col-stats ms-3 ms-sm-0">
                        <div className="numbers">
                          <p className="card-category">{stat.title}</p>
                          <h4 className={`card-title ${stat.textClass}`}>
                            {stat.value}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Table Card Section */}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-end gap-3">
            {/* Title */}
            <h4 className="card-title mb-0">
              PhonePe Subscription Transactions ({totalFiltered})
            </h4>

            {/* Filters */}
            <div className="d-flex flex-wrap align-items-end gap-3 ms-auto">
              {/* Search Bar */}
              <form
                onSubmit={handleSearchSubmit}
                className="position-relative"
                style={{ width: "240px" }}
              >
                <input
                  className="form-control shadow-sm px-3 py-2 border-2 w-100"
                  type="text"
                  placeholder="Search name, phone, sub ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </form>

              {/* Txn Status Filter */}
              <div className="d-flex flex-column">
                <label className="form-label mb-1">Txn Status</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ height: "38px" }}
                >
                  <option value="ALL">All Txn Statuses</option>
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="FAILED">FAILED</option>
                  <option value="PAUSED">PAUSED</option>
                  <option value="ACTIVE">RESUMED</option>
                  <option value="REVOKED">REVOKED</option>
                </select>
              </div>

              {/* Mandate Status Filter */}
              <div className="d-flex flex-column">
                <label className="form-label mb-1">Mandate Status</label>
                <select
                  className="form-select"
                  value={mandateFilter}
                  onChange={(e) => {
                    setMandateFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ height: "38px" }}
                >
                  <option value="ALL">All Mandates</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="PAUSED">PAUSED</option>
                  <option value="REVOKED">REVOKED</option>
                  <option value="FAILED">FAILED</option>
                  <option value="PENDING">PENDING</option>
                </select>
              </div>

              {/* From Date */}
              <div className="d-flex flex-column">
                <label htmlFor="fromDate" className="form-label mb-1">
                  From
                </label>
                <input
                  id="fromDate"
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {/* To Date */}
              <div className="d-flex flex-column">
                <label htmlFor="toDate" className="form-label mb-1">
                  To
                </label>
                <input
                  id="toDate"
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {/* Filter Button */}
              <button
                className="btn btn-primary rounded-pill px-4"
                style={{ height: "38px" }}
                onClick={() => {
                  setPage(1);
                  fetchTransactions(1);
                }}
              >
                Filter
              </button>

              {/* Reset Button */}
              <button
                className="btn btn-icon btn-round btn-dark py-3 px-3"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Reset Filters"
                onClick={handleResetFilters}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date & Time</th>
                    <th>Subscriber / User</th>
                    <th>Subscription ID</th>
                    <th>Merchant Order ID</th>
                    <th>Amount</th>
                    <th>Txn Status</th>
                    <th>Mandate Status</th>
                    <th>Reason / Details</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center">
                        <div
                          style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 9999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            className="spinner-border text-success"
                            role="status"
                            style={{ width: "3rem", height: "3rem" }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No PhonePe Transactions Found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx, idx) => {
                      const isSuccess = tx.status === "SUCCESS";
                      return (
                        <tr key={tx.id || idx}>
                          <td>{(page - 1) * pageSize + idx + 1}</td>
                          <td>
                            <span className="fw-bold d-block">
                              {tx.created_at
                                ? new Date(tx.created_at).toLocaleDateString()
                                : "N/A"}
                            </span>
                            <small className="text-muted">
                              {tx.created_at
                                ? new Date(tx.created_at + "Z").toLocaleString(
                                    [],
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )
                                : ""}
                            </small>
                          </td>
                          <td>
                            <strong className="d-block text-dark">
                              {tx.full_name || tx.name || "N/A"}
                            </strong>
                            {(tx.phone_number || tx.contact) && (
                              <small className="d-block text-muted">
                                <i className="fas fa-phone me-1"></i>
                                {tx.phone_number || tx.contact}
                              </small>
                            )}
                            {tx.email && (
                              <small className="d-block text-muted">
                                <i className="fas fa-envelope me-1"></i>
                                {tx.email}
                              </small>
                            )}
                          </td>
                          <td>
                            <code>{tx.subscription_id || "N/A"}</code>
                          </td>
                          <td>
                            <code>{tx.merchant_order_id || "N/A"}</code>
                          </td>
                          <td>
                            <strong className="text-success">
                              ₹{Number(tx.amount || 0).toLocaleString()}
                            </strong>
                          </td>
                          <td>
                            {renderTxnBadge(tx.status)}
                          </td>
                          <td>
                            {renderMandateBadge(tx.subscription_status)}
                          </td>
                          <td style={{ maxWidth: "250px" }}>
                            <small
                              className={
                                isSuccess ? "text-muted" : "text-danger fw-bold"
                              }
                            >
                              {tx.reason || "N/A"}
                            </small>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-dark btn-round"
                              onClick={() => handleOpenModal(tx)}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls - Server Side */}
            {!loading && transactions.length > 0 && (
              <div className="mt-3">
                <nav aria-label="Page navigation">
                  <ul className="pagination justify-content-end">
                    {/* First Page */}
                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setPage(1)}>
                        First
                      </button>
                    </li>

                    {/* Previous */}
                    <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      >
                        Previous
                      </button>
                    </li>

                    {/* Page Numbers */}
                    {(() => {
                      const pageNumbers = [];
                      const maxPagesToShow = 5;
                      const startPage =
                        Math.floor((page - 1) / maxPagesToShow) *
                          maxPagesToShow +
                        1;
                      const endPage = Math.min(
                        startPage + maxPagesToShow - 1,
                        totalPages,
                      );

                      for (let i = startPage; i <= endPage; i++) {
                        pageNumbers.push(
                          <li
                            key={i}
                            className={`page-item ${
                              page === i ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setPage(i)}
                            >
                              {i}
                            </button>
                          </li>,
                        );
                      }
                      return pageNumbers;
                    })()}

                    {/* Next */}
                    <li
                      className={`page-item ${
                        page === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          setPage((p) => Math.min(p + 1, totalPages))
                        }
                      >
                        Next
                      </button>
                    </li>

                    {/* Last Page */}
                    <li
                      className={`page-item ${
                        page === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setPage(totalPages)}
                      >
                        Last
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {showModal && selectedTransaction && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Transaction Details (#{selectedTransaction.merchant_order_id})
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6 mb-2">
                      <strong>Subscriber / Donor: </strong>
                      {selectedTransaction.full_name || selectedTransaction.name || "N/A"}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Amount: </strong>₹{Number(selectedTransaction.amount || 0).toLocaleString()}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Transaction Status: </strong>
                      {renderTxnBadge(selectedTransaction.status)}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Mandate Status: </strong>
                      {renderMandateBadge(selectedTransaction.subscription_status)}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Transaction Date: </strong>
                      {selectedTransaction.created_at || "N/A"}
                    </div>
                    <div className="col-md-6 mb-2">
                      <strong>Next Debit / Call Date: </strong>
                      {selectedTransaction.next_call_date || "N/A"}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="fw-bold mb-1">Reason / Note:</label>
                    <div className="p-2 bg-light border rounded">
                      {selectedTransaction.reason || "N/A"}
                    </div>
                  </div>

                  <div>
                    <label className="fw-bold mb-1">
                      Response Payload (JSON):
                    </label>
                    <pre
                      className="bg-dark text-light p-3 rounded"
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      {formatPayload(selectedTransaction.response_payload)}
                    </pre>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhonePeTransactions;
